import { createContext, useContext, useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/AuthContext";
import { Moon, Sun } from "lucide-react";

export const COMPANY_HEX = {
  Cleanmates: "hsl(var(--company-cleanmates))",
  PestGenis: "hsl(var(--company-pestgenis))",
  Pestgenis: "hsl(var(--company-pestgenis))",
  "StainGuard Pro": "hsl(var(--company-stainguard))",
  "Stainguard Pro": "hsl(var(--company-stainguard))",
  Rover: "hsl(var(--company-rover))",
  Limpio: "hsl(var(--company-limpio))",
};

const CompanyCtx = createContext(null);

function brandPalette(hex) {
  const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(hex || "");
  if (!match) return null;
  const value = match[1].length === 3 ? [...match[1]].map((part) => part + part).join("") : match[1];
  const [componentR, componentG, componentB] = [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16) / 255);
  const max = Math.max(componentR, componentG, componentB);
  const min = Math.min(componentR, componentG, componentB);
  const delta = max - min;
  let hue = 0;
  if (delta) {
    if (max === componentR) hue = ((componentG - componentB) / delta) % 6;
    else if (max === componentG) hue = (componentB - componentR) / delta + 2;
    else hue = (componentR - componentG) / delta + 4;
  }
  hue = Math.round((hue * 60 + 360) % 360);
  if (hue >= 50 && hue <= 180) return null;
  const lightness = (max + min) / 2;
  const saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
  const hsl = (light, sat = saturation) => `${hue} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
  return {
    primary: hsl(lightness),
    hover: hsl(Math.max(0.18, lightness - 0.07)),
    light: hsl(0.95, Math.max(0.12, saturation * 0.55)),
  };
}

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [company, setCompanyState] = useState(() => localStorage.getItem("cm_company") || "all");
  const setCompany = (c) => { localStorage.setItem("cm_company", c); setCompanyState(c); };
  useEffect(() => {
    const root = document.documentElement;
    const palette = brandPalette(user?.tenant?.brand_color);
    const properties = ["--brand-primary", "--brand-hover", "--brand-light"];
    if (!palette) {
      properties.forEach((property) => root.style.removeProperty(property));
      return;
    }
    const values = [palette.primary, palette.hover, palette.light, palette.dark, palette.foreground, palette.darkForeground];
    properties.forEach((property, index) => root.style.setProperty(property, values[index]));
  }, [user?.tenant?.id, user?.tenant?.brand_color]);
  return <CompanyCtx.Provider value={{ company, setCompany }}>{children}</CompanyCtx.Provider>;
}

export const useCompany = () => useContext(CompanyCtx);

export function CompanySwitcher() {
  const { company, setCompany } = useCompany();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    if (user?.is_platform_admin) api.get("/platform/workspaces").then((r) => setCompanies(r.data)).catch(() => {});
    else api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, [user?.is_platform_admin]);
  if (!user || user.role === "team_leader") return null;
  if (user.is_platform_admin) {
    if (!user.active_tenant_id) return null;
    return (
      <label className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="company-switcher">
        <span className="hidden sm:inline">Workspace</span>
        <select aria-label="Switch company workspace" data-testid="workspace-switcher" value={user.active_tenant_id}
          onChange={(event) => {
            const tenant = companies.find((item) => item.id === event.target.value);
            if (!tenant) return;
            localStorage.setItem("cm_active_tenant", tenant.id);
            localStorage.setItem("cm_last_tenant", tenant.id);
            window.location.assign("/admin");
          }}
          className="h-9 max-w-52 rounded-md border bg-card px-2 text-sm font-medium text-foreground">
          {companies.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
    );
  }
  const pill = (active, color) =>
    `text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${active ? "text-inverse shadow-sm" : "bg-card hover:bg-accent text-muted-foreground"}`;
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid="company-switcher">
      <button data-testid="company-pill-all" onClick={() => setCompany("all")}
        className={pill(company === "all")} style={company === "all" ? { background: "hsl(var(--foreground))", borderColor: "hsl(var(--foreground))" } : {}}>
        All Companies
      </button>
      {companies.map((c) => (
        <button key={c.code} data-testid={`company-pill-${c.code}`} onClick={() => setCompany(c.name)}
          className={pill(company === c.name)}
          style={company === c.name ? { background: COMPANY_HEX[c.name] || "hsl(var(--primary))", borderColor: COMPANY_HEX[c.name] || "hsl(var(--primary))" } : {}}>
          {c.name}
        </button>
      ))}
    </div>
  );
}

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  const [themePreference, setThemePreference] = useState(() => localStorage.getItem("cm_theme"));
  const [dark, setDark] = useState(() => {
    if (themePreference) return themePreference === "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);
  useEffect(() => {
    if (themePreference) return undefined;
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return undefined;
    const updateFromSystem = (event) => setDark(event.matches);
    if (media.addEventListener) media.addEventListener("change", updateFromSystem);
    else media.addListener?.(updateFromSystem);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", updateFromSystem);
      else media.removeListener?.(updateFromSystem);
    };
  }, [themePreference]);
  const chooseTheme = (nextDark) => {
    const nextPreference = nextDark ? "dark" : "light";
    localStorage.setItem("cm_theme", nextPreference);
    setThemePreference(nextPreference);
    setDark(nextDark);
  };
  return <ThemeCtx.Provider value={{ dark, setDark: chooseTheme }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);

export function ThemeToggle() {
  const { dark, setDark } = useTheme();
  return (
    <button data-testid="theme-toggle" onClick={() => setDark(!dark)} title="Toggle dark mode"
      className="w-9 h-9 rounded-full border bg-card hover:bg-accent transition-colors flex items-center justify-center text-muted-foreground">
      {dark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}
